import type { RenderingConfigKey, RenderingMode } from './utils.type';

export type RenderModeValue = (typeof RenderMode)[keyof typeof RenderMode];

export type RenderingConfig = Map<
    RenderingConfigKey[keyof RenderingConfigKey],
    (typeof RenderMode)[keyof typeof RenderMode] | boolean
>;

/**
 * @memberof SharedComponents
 * @class Config
 * @alias config
 * @classdesc
 * Provides configuration for components.
 * Default configuration:
 * - Render mode: 'synchronous'
 * - Debug mode: false
 * @example
 * import {configureUIComponents, renderSync, debug} from 'shared-components';
 * configureUIComponents(renderSync(), debug());
 */

/**
 * @memberof Config
 * @type {{Synchronous: 'SYNCHRONOUS', Asynchronous: 'ASYNCHRONOUS'}}
 */
const RenderMode: RenderingMode = {
    Synchronous: 'SYNCHRONOUS',
    Asynchronous: 'ASYNCHRONOUS',
} as const;

const Config: RenderingConfigKey = {
    RenderMode: 'RENDER_MODE',
    DebugMode: 'DEBUG_MODE',
    LegacyBooleans: 'LEGACY_BOOLEANS',
} as const;

/**
 * Stores global configuration for components.
 * @type {Map<string, any>}
 */
// @ts-expect-error - TODO: refactor during GUI-3369
const globalConfig: RenderingConfig = new Map([
    [Config.RenderMode, RenderMode.Synchronous],
    [Config.DebugMode, false],
]);

/**
 * Enables asynchronous rendering for components.
 * @memberof Config
 * @returns {(function(): void)}
 */
const renderAsync = (): (() => void) => {
    return () => setRenderMode(RenderMode.Asynchronous);
};

/**
 * Enables synchronous rendering for components.
 * @memberof Config
 * @returns {(function(): void)}
 */
const renderSync = (): (() => void) => {
    return () => setRenderMode(RenderMode.Synchronous);
};

/**
 * Enables debug mode.
 * @memberof Config
 * @param {boolean} [value]
 * @returns {(function(): void)}
 */
const debug = (value: boolean = true): (() => void) => {
    return () => {
        setDebugMode(value);
    };
};

/**
 * Used for lazy loading components.
 * @memberof Config
 * @param {function(): Promise} loadAction
 * @param {string | Element} [selector]
 * @returns {(function(): void)}
 */
const components = (
    loadAction: () => Promise<void>,
    selector: string | Element
): (() => void) => {
    return () => {
        loadAction().then(() => {
            reportComponents(selector);
        });
    };
};

/**
 * Warns if component did not render.
 * @param {string | Element} [selector]
 */
const reportComponents = (selector: string | Element): void => {
    if (!getDebugMode()) {
        return;
    }
    const rootNode =
        typeof selector === 'string'
            ? document.querySelector(selector)
            : selector || document;
    const elementsByTagName = rootNode?.getElementsByTagName('*');
    const tags: Array<HTMLElement> = Array.from(
        elementsByTagName as unknown as Array<HTMLElement>
    )
        .filter((tag: HTMLElement) => tag.tagName.startsWith('UI-'))
        .filter((tag: HTMLElement) => !('state' in tag));
    tags.forEach((tag: HTMLElement) => {
        const tagName = tag.localName;

        console.warn(
            `UI component <${tagName}> not loaded. Load module in the configureUIComponents function.`,
            tag
        );
    });
};

/**
 * Sets debug mode.
 * @memberof Config
 * @param {boolean} value
 */
function setDebugMode(value: boolean): void {
    globalConfig.set(Config.DebugMode, value);
}

/**
 * Returns true if debug mode is enabled.
 * @memberof Config
 * @returns {boolean | string | undefined}
 */
function getDebugMode(): RenderModeValue | boolean | undefined {
    return globalConfig.get(Config.DebugMode);
}

/**
 * Sets components rendering mode.
 * @memberof Config
 * @param {RenderingMode} mode
 */
function setRenderMode(mode: RenderModeValue) {
    globalConfig.set(Config.RenderMode, mode);
}

/**
 * Returns rendering mode.
 * @memberof Config
 * @returns {RenderingMode}
 */
function getRenderMode(): RenderModeValue | boolean | undefined {
    return globalConfig.get(Config.RenderMode);
}

/**
 * Provides configuration for components.
 * @memberof Config
 * @param {...Array<() => void} settings
 */
const configureUIComponents = (...settings: Array<() => void>) => {
    settings.forEach((setting) => setting());
};

export {
    configureUIComponents,
    renderAsync,
    renderSync,
    debug,
    components,
    getDebugMode,
    setDebugMode,
    setRenderMode,
    getRenderMode,
    RenderMode,
};
